import React, { useState, useRef } from 'react';
import type { ReactNode } from 'react';
import { ModalTransition } from '@atlaskit/modal-dialog';
import type { ModalController } from '../bridge/types';

type UseRuntimeModalControllerResult = {
	modalController: ModalController;
	runtimeModal: ReactNode;
};

/**
 * Owns the single modal slot for WorkerWebRuntime.
 *
 * Bridge methods call `modalController.show(content)` to imperatively render
 * any ReactNode into the slot, and `modalController.hide()` to clear it.
 * Only one modal can be displayed at a time — `show` always replaces the
 * current content.
 *
 * The `modalController` object is stable for the lifetime of the component
 * (backed by a ref) so it is safe to store in BridgeContext and capture in
 * bridge method closures without stale-closure concerns.
 */
export const useRuntimeModalController = (): UseRuntimeModalControllerResult => {
	const [runtimeModalContent, setRuntimeModalContent] = useState<ReactNode>(null);
	const [isRuntimeModalOpen, setIsRuntimeModalOpen] = useState(false);
	const [isRuntimeModalAnimated, setIsRuntimeModalAnimated] = useState(true);

	// Stable object — never recreated, always points at the current setter.
	const modalControllerRef = useRef<ModalController>({
		show: (content: ReactNode, animated = true) => {
			setRuntimeModalContent(content);
			setIsRuntimeModalAnimated(animated);
			setIsRuntimeModalOpen(true);
		},
		hide: () => setIsRuntimeModalOpen(false),
	});

	const runtimeModal = isRuntimeModalAnimated ? (
		<ModalTransition>{isRuntimeModalOpen ? runtimeModalContent : null}</ModalTransition>
	) : isRuntimeModalOpen ? (
		runtimeModalContent
	) : null;

	return { runtimeModal, modalController: modalControllerRef.current };
};
